import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onViewDetails, onViewDemo, onViewCode }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -10 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="glass-morphism rounded-xl overflow-hidden cyber-border hover:cyber-border-active transition-all duration-500">
        {/* Project Image */}
        <div className="relative h-48 overflow-hidden">
          <Image
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
          
          {/* Status Badge */}
          <div className="absolute top-4 right-4">
            <div className={`px-3 py-1 rounded-full text-xs font-mono ${
              project.status === 'live' ?'bg-brand-green/20 text-brand-green cyber-border' 
                : project.status === 'development' ?'bg-brand-orange/20 text-brand-orange cyber-border' :'bg-brand-purple/20 text-brand-purple cyber-border'
            }`}>
              <div className="flex items-center space-x-1">
                <div className={`w-2 h-2 rounded-full animate-pulse ${
                  project.status === 'live' ? 'bg-brand-green' : 
                  project.status === 'development' ? 'bg-brand-orange' : 'bg-brand-purple'
                }`} />
                <span>{project.status.toUpperCase()}</span>
              </div>
            </div>
          </div>

          {/* Overlay Actions */}
          <motion.div
            className="absolute inset-0 bg-background/90 flex items-center justify-center space-x-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => onViewDemo(project)}
              className="cyber-border hover:cyber-glow"
            >
              Demo
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Code"
              iconPosition="left"
              onClick={() => onViewCode(project)}
              className="cyber-border hover:cyber-glow"
            >
              Code
            </Button>
          </motion.div>
        </div>

        {/* Project Content */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-exo text-xl font-bold text-foreground group-hover:text-brand-cyan transition-colors duration-300">
              {project.title}
            </h3>
            <div className="flex items-center space-x-1">
              {project.featured && (
                <Icon name="Star" size={16} className="text-brand-orange fill-current" />
              )}
              <Icon name="ExternalLink" size={16} className="text-text-secondary group-hover:text-brand-cyan transition-colors duration-300" />
            </div>
          </div>

          <p className="text-text-secondary text-sm mb-4 line-clamp-2 md:line-clamp-none">
            {project.description}
          </p>

          {/* Tech Stack - Hidden on mobile, visible on larger screens */}
          <div className="hidden md:flex flex-wrap gap-2 mb-4">
            {project.technologies.slice(0, 4).map((tech, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-surface/50 text-brand-cyan text-xs font-mono rounded cyber-border"
              >
                {tech}
              </span>
            ))}
            {project.technologies.length > 4 && (
              <span className="px-2 py-1 bg-surface/50 text-text-secondary text-xs font-mono rounded cyber-border">
                +{project.technologies.length - 4}
              </span>
            )}
          </div>

          {/* Metrics - Hidden on mobile, visible on larger screens */}
          <div className="hidden md:grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-brand-cyan font-mono text-sm font-bold">
                {project.metrics.performance}
              </div>
              <div className="text-text-tertiary text-xs">Performance</div>
            </div>
            <div className="text-center">
              <div className="text-brand-green font-mono text-sm font-bold">
                {project.metrics.uptime}
              </div>
              <div className="text-text-tertiary text-xs">Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-brand-orange font-mono text-sm font-bold">
                {project.metrics.users}
              </div>
              <div className="text-text-tertiary text-xs">Users</div>
            </div>
          </div>

          {/* Mobile Indicator */}
          <div className="flex md:hidden items-center justify-center text-text-secondary text-sm mb-4">
            <Icon name="Info" size={16} className="mr-2" />
            <span>Tap to view details</span>
          </div>

          {/* Action Button */}
          <Button
            variant="outline"
            fullWidth
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => onViewDetails(project)}
            className="cyber-border hover:cyber-border-active hover:cyber-glow transition-all duration-300"
          >
            Explore Project
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;